import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  TagIcon,
  CalendarIcon,
  ArrowTopRightOnSquareIcon,
  ClockIcon,
  ArrowUpRightIcon,
  ArrowDownLeftIcon,
  AdjustmentsHorizontalIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import financeApi from '../../../services/financeApi';
import Notification from '../../../components/notification/notification';
import companyApi from '../../../services/companyApi';
import {
  TWButton,
  TWSelect,
  TWDataTable,
  PageHeader,
  UserAvatar,
  EmptyState,
} from '../../../components/ui';
import { headerCls, DataTableSkeleton } from '../../../data/columns';
import { formatCurrency } from '../../../utils/baseUtils';
import './account-activity.css';

const cn = (...classes) => classes.filter(Boolean).join(' ');

const AccountActivity = ({ embedded = false }) => {
  const globalCompany = useSelector((state) => state.companyReducer.selected);
  const [loading, setLoading] = useState({ listLoading: true });
  const [financeTypeList, setFinanceTypeList] = useState([]);
  const [companyList, setCompanyList] = useState([]);
  const [search, setSearch] = useState({
    financetypeid: '',
    companyid: '',
  });
  const [notf, setNotf] = useState({
    display: false,
    type: 'error',
    message: '',
  });

  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 10,
    totalRecords: null,
  });
  const [financeList, setFinanceList] = useState([]);

  const getFinanceList = async () => {
    try {
      setLoading((prev) => ({ ...prev, listLoading: true }));
      let query = `?pageNumber=${pagination.pageNumber}&pageSize=${pagination.pageSize}`;
      if (search.financetypeid)
        query += `&financetypeids=${search.financetypeid}`;
      if (search.companyid) query += `&companyid=${search.companyid}`;

      const list = await financeApi.list(query);
      if (list.data.data.length > 0) {
        setFinanceList(list.data.data);
        setPagination({
          pageNumber: list.data.pageNumber,
          pageSize: list.data.pageSize,
          totalRecords: list.data.totalRecords,
        });
      } else {
        setFinanceList([]);
        setPagination((prev) => ({ ...prev, totalRecords: 0 }));
      }
    } catch (error) {
      setNotf((prev) => ({ ...prev, message: error, display: true }));
    } finally {
      setLoading((prev) => ({ ...prev, listLoading: false }));
    }
  };

  const getFinanceTypeList = async () => {
    try {
      // Every type, not the server's first page of ten — this list is a picker.
      const list = await financeApi.getTypeList('?pageSize=1000');
      setFinanceTypeList(list.data.data);
    } catch (error) {
      setNotf((prev) => ({ ...prev, message: error, display: true }));
    }
  };

  const getCompanyList = async () => {
    try {
      const query = `?pageSize=1000`;
      const list = await companyApi.list(query);
      setCompanyList(list.data.data?.length > 0 ? list.data.data : []);
    } catch (error) {
      setNotf((prev) => ({ ...prev, message: error, display: true }));
    }
  };

  useEffect(() => {
    getFinanceTypeList();
    getCompanyList();
    getFinanceList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalCompany.id, pagination.pageNumber, pagination.pageSize, search]);

  const hasFilters = Boolean(search.companyid || search.financetypeid);

  // Task 4 (admin Finance console): embedded is the console's Ledger tab —
  // no page shell, no PageHeader (and no back link, since there is nowhere
  // to go back to inside the console), an <h2>Ledger</h2> in its place. The
  // non-embedded render below stays exactly what it was.
  const body = (
    <>
      {embedded ? (
        <div>
          <h2 className="text-base font-bold text-slate-900 dark:text-slate-100">Legacy ledger</h2>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {"Every statement, fee and payout ever written to a company's legacy ledger."}
          </p>
        </div>
      ) : (
        <PageHeader
          title="Transaction History"
          description="View and keep track of all company financial transactions."
          backTo="/dashboard/finance"
          backLabel="Back to Finance"
        />
      )}

        <section className="reveal rounded-md border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800/70">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-slate-400 dark:text-slate-500">
              <AdjustmentsHorizontalIcon className="h-4 w-4" />
              Filters
            </div>
            <div className="flex-1 sm:ml-4">
              <TWSelect
                label="Company"
                placeholder="All companies"
                onChange={(e) =>
                  setSearch((prev) => ({ ...prev, companyid: e || '' }))
                }
                options={companyList.map((a) => ({
                  label: a.name,
                  value: a.id,
                }))}
                value={search.companyid || undefined}
              />
            </div>
            <div className="flex-1">
              <TWSelect
                label="Finance Type"
                placeholder="All types"
                onChange={(e) =>
                  setSearch((prev) => ({ ...prev, financetypeid: e || '' }))
                }
                options={financeTypeList.map((a) => ({
                  label: a.name,
                  value: a.id,
                }))}
                value={search.financetypeid || undefined}
              />
            </div>
            {hasFilters && (
              <TWButton
                icon={TrashIcon}
                label="Clear"
                onClick={() => setSearch({ companyid: '', financetypeid: '' })}
              />
            )}
          </div>
        </section>

        {loading.listLoading ? (
          <DataTableSkeleton
            rows={6}
            headers={[
              { label: 'Company', w: 'w-40', shape: 'avatar' },
              { label: 'Type', w: 'w-24', shape: 'pill' },
              { label: 'Explanation', w: 'w-48' },
              { label: 'Code', w: 'w-16' },
              { label: 'Date', w: 'w-24' },
              { label: 'Amount', align: 'right', w: 'w-24', shape: 'pill' },
            ]}
          />
        ) : financeList.length === 0 ? (
          <EmptyState
            variant="dashed"
            icon={ClockIcon}
            title="No transactions found"
            description={
              hasFilters
                ? 'Try adjusting your filters to see more results.'
                : 'Account activity will appear here once transactions are recorded.'
            }
          />
        ) : (
          <div className="reveal">
            <TWDataTable
              columns={[
                {
                  title: <span className={headerCls}>Company</span>,
                  key: 'company',
                  render: (_, finance) => (
                    <div className="flex items-center gap-3 min-w-0">
                      {finance.company?.logo ? (
                        <img
                          alt="logo"
                          className="h-10 w-10 shrink-0 rounded-md object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                          src={finance.company.logo}
                        />
                      ) : (
                        <UserAvatar
                          name={finance.company?.name}
                          size="md"
                          className="!rounded-md"
                        />
                      )}
                      <div className="min-w-0 flex-1">
                        <Link
                          to={`/dashboard/companies/${finance.company?.id}`}
                          className="block truncate text-sm font-semibold text-slate-900 hover:text-blue-600 dark:text-slate-100 dark:hover:text-sky-400"
                        >
                          {finance.company?.name}
                        </Link>
                        {finance.company?.subscription?.name && (
                          <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                            {finance.company.subscription.name}
                          </p>
                        )}
                      </div>
                    </div>
                  ),
                },
                {
                  title: <span className={headerCls}>Type</span>,
                  key: 'type',
                  render: (_, finance) => (
                    <span className="inline-flex items-center gap-1.5 rounded-md bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700 dark:bg-slate-700/60 dark:text-slate-200">
                      <TagIcon className="h-3 w-3 text-slate-400" />
                      <span className="truncate">
                        {finance.financetype?.name || '—'}
                      </span>
                    </span>
                  ),
                },
                {
                  title: <span className={headerCls}>Explanation</span>,
                  key: 'explanation',
                  render: (_, finance) =>
                    finance.details?.length > 0 ? (
                      <Link
                        to={`/dashboard/companies/${finance.companyid}/orders?financeid=${finance.id}`}
                        className="flex min-w-0 items-center gap-1 text-sm text-blue-600 hover:text-blue-700 dark:text-sky-400 dark:hover:text-sky-300"
                      >
                        <span className="truncate">{finance.description}</span>
                        <ArrowTopRightOnSquareIcon className="h-3 w-3 shrink-0" />
                      </Link>
                    ) : (
                      <span className="text-sm text-slate-600 dark:text-slate-300">
                        {finance.description || '—'}
                      </span>
                    ),
                },
                {
                  title: <span className={headerCls}>Code</span>,
                  key: 'code',
                  render: (_, finance) =>
                    finance.code ? (
                      <span className="inline-block max-w-full truncate rounded-md bg-slate-100 px-2 py-0.5 font-mono text-xs text-slate-700 dark:bg-slate-700/50 dark:text-slate-200">
                        {finance.code}
                      </span>
                    ) : (
                      <span className="text-slate-300 dark:text-slate-600">
                        —
                      </span>
                    ),
                },
                {
                  title: <span className={headerCls}>Date</span>,
                  key: 'date',
                  render: (_, finance) => (
                    <div className="flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400">
                      <CalendarIcon className="h-3.5 w-3.5 shrink-0" />
                      <span>
                        {moment
                          .utc(finance.createdate)
                          .local()
                          .format('MM/DD/YYYY')}
                      </span>
                    </div>
                  ),
                },
                {
                  title: (
                    <span className={`${headerCls} block text-right`}>
                      Amount
                    </span>
                  ),
                  key: 'amount',
                  align: 'right',
                  render: (_, finance) => {
                    const amount = Number(finance.amount) || 0;
                    const isPositive = amount >= 0;
                    return (
                      <span
                        className={cn(
                          'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ring-1',
                          isPositive
                            ? 'bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:ring-emerald-400/30'
                            : 'bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-400 dark:ring-rose-400/30',
                        )}
                      >
                        {isPositive ? (
                          <ArrowUpRightIcon className="h-3 w-3" />
                        ) : (
                          <ArrowDownLeftIcon className="h-3 w-3" />
                        )}
                        {formatCurrency(amount)}
                      </span>
                    );
                  },
                },
              ]}
              data={financeList}
              rowKey="id"
              pagination={
                pagination.totalRecords > pagination.pageSize
                  ? {
                      current: pagination.pageNumber,
                      pageSize: pagination.pageSize,
                      total: pagination.totalRecords,
                      showTotal: (total, range) =>
                        `Showing ${range[0]} to ${range[1]} of ${total} transactions`,
                      onChange: (page) =>
                        setPagination((prev) => ({
                          ...prev,
                          pageNumber: page,
                        })),
                      showSizeChanger: false,
                    }
                  : undefined
              }
            />
          </div>
        )}
    </>
  );

  if (embedded) {
    return (
      <>
        <section className="space-y-4">{body}</section>
        <Notification
          display={notf.display}
          type={notf.type}
          message={notf.message}
          onClose={() => setNotf((prev) => ({ ...prev, display: false }))}
        />
      </>
    );
  }

  return (
    <div className="activity-shell min-h-[calc(100vh-4rem)] bg-white px-4 py-6 dark:bg-slate-900/40 sm:px-6 lg:px-8 lg:py-8">
      <div className="mx-auto max-w-5xl space-y-8">{body}</div>
      <Notification
        display={notf.display}
        type={notf.type}
        message={notf.message}
        onClose={() => setNotf((prev) => ({ ...prev, display: false }))}
      />
    </div>
  );
};

export default AccountActivity;
